import {useMemo} from 'react'

import {useAppSelector} from '../../../../hooks/react-redux'
import {defaultHeaderName, defaultHeaderValue, defaultValue} from '../Parameters.constants'
import type {ParameterGroup, VirtualizerItem} from '../Parameters.types'
import {ItemType, ParameterCategory} from '../Parameters.types'

import type {Property} from 'src/services/rest'

interface Props {
  parameters: {
    category: ParameterCategory
    original?: Record<string, string>
    groups: {
      group: ParameterGroup
      header?: {
        name?: string
        value?: string
      }
      withDescription?: boolean
      items: readonly Property[]
    }[]
  }[]
  highlight: string
}

export const useParametersTree = (props: Props): VirtualizerItem[] => {
  const {parameters, highlight} = props

  const expanded = useAppSelector(state => state.parameterGroups.expanded)

  return useMemo(() => {
    const items = parameters.flatMap(category => {
      const isCategoryEmpty = category.groups.every(group => group.items.length === 0)

      if (isCategoryEmpty) {
        return []
      }

      return [
        ...(![ParameterCategory.AGENT, ParameterCategory.AGENT_TYPE].includes(category.category)
          ? [
              {
                type: ItemType.CATEGORY_HEADER as const,
                key: `${ItemType.CATEGORY_HEADER}-${category.category}`,
                category: category.category,
              },
            ]
          : []),
        ...category.groups
          .filter(group => group.items.length !== 0)
          .flatMap(group => [
            {
              type: ItemType.GROUP_HEADER as const,
              group: group.group,
              category: category.category,
              matches: highlight ? group.items.length : undefined,
              key: `${ItemType.GROUP_HEADER}-${category.category}-${group.group}`,
            },
            ...(expanded[category.category][group.group]
              ? [
                  ...(group.withDescription
                    ? [
                        {
                          type: ItemType.GROUP_DESCRIPTION as const,
                          group: group.group,
                          key: `${ItemType.GROUP_DESCRIPTION}-${group.group}`,
                        },
                      ]
                    : []),
                  {
                    type: ItemType.TABLE_HEADER as const,
                    key: `${ItemType.TABLE_HEADER}-${category.category}-${group.group}`,
                    name: group.header?.name ?? defaultHeaderName,
                    value: group.header?.value ?? defaultHeaderValue,
                  },
                  ...group.items.map(item => {
                    const name = item.name || defaultValue
                    const value = item.value || defaultValue

                    const key = `${ItemType.PARAMETER}-${category.category}-${group.group}-${name}`

                    if (
                      category.original &&
                      item.name &&
                      category.original[item.name] !== item.value
                    ) {
                      return category.original[item.name] == null
                        ? {
                            type: ItemType.ADDED_PARAMETER as const,
                            name,
                            value,
                            highlight,
                            key,
                          }
                        : {
                            type: ItemType.MODIFIED_PARAMETER as const,
                            name,
                            value,
                            highlight,
                            defaultValue: category.original[item.name] || defaultValue,
                            key,
                          }
                    }
                    return {
                      type: ItemType.PARAMETER as const,
                      name,
                      value,
                      highlight,
                      key,
                    }
                  }),
                ]
              : []),
          ]),
      ]
    })

    return items.length === 0
      ? [
          {
            type: ItemType.EMPTY as const,
            key: `${ItemType.EMPTY}`,
          },
        ]
      : items
  }, [parameters, expanded, highlight])
}
